/**
 * Local calendar-day helpers.
 *
 * `toISOString()` is UTC, so using it to derive a 'YYYY-MM-DD' key from a Date
 * built with `new Date(y, m, d)` (which is LOCAL midnight) silently shifts the
 * day for most of the world: in IST, local midnight on the 14th is 18:30 UTC on
 * the 13th, so the key comes back a day early and records land on the wrong
 * calendar cell. Anything that turns a Date into a day string for display,
 * lookup, or an <input type="date"> must go through here.
 */

/** Local 'YYYY-MM-DD' for a Date — never toISOString, which is UTC. */
export const localDateStr = (d = new Date()) => {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

/** Today as a local 'YYYY-MM-DD' — for `max` on a date input, "is today", etc. */
export const todayStr = () => localDateStr(new Date());

/** Local 'YYYY-MM' for a Date — the month key the attendance APIs take. */
export const localMonthStr = (d = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

/** Local 'YYYY-MM-DD' n days from now (negative for the past). */
export const daysFromTodayStr = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return localDateStr(d);
};

/** Is this a well-formed 'YYYY-MM-DD'? */
export const isIsoDay = (s) => typeof s === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s);

/**
 * Any DATE/DATETIME the API returns, as a bare 'YYYY-MM-DD', or null.
 *
 * Slicing the string is the whole point: `new Date('2026-09-11')` is UTC
 * midnight, so deriving a day from it lands on the 10th anywhere behind UTC.
 * Compare and store days as strings — 'YYYY-MM-DD' sorts and compares
 * correctly with `<`, `>` and `===`, in every zone, with no Date in the loop.
 */
export const apiDayStr = (v) => {
  if (!v) return null;
  const s = v instanceof Date ? localDateStr(v) : String(v).slice(0, 10);
  return isIsoDay(s) && !s.startsWith("0000") ? s : null;
};

/** 'YYYY-MM-DD' n days on from a day string. Stepped through UTC so no local
 *  offset or DST change can move the boundary. */
export const addDaysStr = (isoDay, n) => {
  if (!isIsoDay(isoDay)) return isoDay;
  const d = new Date(`${isoDay}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

/**
 * A DATETIME from the API as a Date, or null.
 *
 * Server timestamps (created_at, reviewed_at, …) are instants, and they are
 * written by a MySQL host running UTC. Endpoints that pass the raw column
 * through hand over a bare 'YYYY-MM-DD HH:MM:SS' with no zone on it, and
 * `new Date()` reads a bare stamp as the VIEWER's local time — so a request
 * raised at 21:42 in IST came back stamped 16:12. A bare stamp is UTC and is
 * read as UTC here; one that already carries a zone ('…Z', '+05:30') is
 * parsed as it stands.
 *
 * Instants only. A calendar day ('2026-09-11') is not an instant — use
 * apiDayStr for those, which never puts a Date in the loop.
 */
export const parseApiDateTime = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const raw = String(value).trim();
  if (!raw || raw.startsWith("0000")) return null;
  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw);
  const iso = isIsoDay(raw)
    ? `${raw}T00:00:00Z`
    : `${raw.replace(" ", "T")}${hasZone ? "" : "Z"}`;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** A DATETIME from the API in the viewer's own time zone, or '—'. */
export const formatApiDateTime = (value, options) => {
  const date = parseApiDateTime(value);
  return date ? date.toLocaleString(undefined, options) : "—";
};

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** '2026-09-07' → 'Mon Sep 07 2026'. Formatted from the string's own parts, so
 *  it can never render the previous day the way `new Date(s).toDateString()`
 *  does behind UTC. */
export const formatDayLong = (isoDay) => {
  if (!isIsoDay(isoDay)) return String(isoDay ?? "");
  const dow = DOW[new Date(`${isoDay}T00:00:00Z`).getUTCDay()];
  return `${dow} ${MON[Number(isoDay.slice(5, 7)) - 1]} ${isoDay.slice(8, 10)} ${isoDay.slice(0, 4)}`;
};
